import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import { Button, Card, CardBody, Col, Row, Table } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp, faChartBar, faTable } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../common/Flex'
import { grays, themeColors } from '../../../helpers/utils'
import { formatNumber, formatPercent, formatPeriod, formatShortPeriod } from '../../rewards/rewardsUtils'

// Antes del programa en gris (contexto) y con Rewards en el color de la marca
// (lo que se quiere mirar). Par validado con el script de dataviz: se
// distinguen con daltonismo y ambos pasan 3:1 contra el fondo blanco.
const BEFORE_COLOR = grays[600]
const AFTER_COLOR = themeColors.primary

// Con menos meses del programa la comparación todavía dice poco.
const MIN_PERIODS_FOR_TREND = 3

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

const RateTile = ({ title, stats, highlight }) => (
  <div className="text-center px-2">
    <h6 className="text-600 fs--1 mb-1">{title}</h6>
    {stats ? (
      <>
        <p className={`fs-3 font-weight-bold mb-0 ${highlight ? 'text-primary' : 'text-900'}`}>
          {formatPercent(stats.rate_percent)}
        </p>
        <p className="fs--2 text-500 mb-0">
          {formatNumber(stats.on_time)} de {formatNumber(stats.due)} cuotas · {stats.periods}{' '}
          {stats.periods === 1 ? 'mes' : 'meses'}
        </p>
      </>
    ) : (
      <p className="fs--1 text-500 mb-0 mt-2">Sin datos todavía</p>
    )}
  </div>
)

const Delta = ({ before, after }) => {
  if (!before || !after) return null
  const points = Math.round((after.rate_percent - before.rate_percent) * 10) / 10
  const lateBefore = 100 - before.rate_percent
  const lateAfter = 100 - after.rate_percent

  return (
    <div className="text-center mt-3">
      <span className={`fs-1 font-weight-semi-bold ${points > 0 ? 'text-success' : 'text-700'}`}>
        <FontAwesomeIcon icon={points >= 0 ? faArrowUp : faArrowDown} className="mr-1 fs-0" />
        {points > 0 ? '+' : ''}
        {formatNumber(points, 1)} puntos de puntualidad
      </span>
      <p className="fs--1 text-600 mb-0">
        Las cuotas pagadas fuera de plazo pasaron del <b className="text-900">{formatPercent(lateBefore)}</b> al{' '}
        <b className="text-900">{formatPercent(lateAfter)}</b>.
      </p>
    </div>
  )
}

// Solo las series que aparecen en el gráfico.
const Legend = ({ monthly }) => (
  <Flex align="center" className="fs--1 text-700 flex-wrap">
    {[
      ['Antes de Rewards', BEFORE_COLOR, monthly.some((row) => !row.with_rewards)],
      ['Con Rewards', AFTER_COLOR, monthly.some((row) => row.with_rewards)]
    ]
      .filter(([, , present]) => present)
      .map(([label, color]) => (
      <span key={label} className="d-inline-flex align-items-center mr-3">
        <span className="d-inline-block rounded mr-1" style={{ width: 10, height: 10, background: color }} />
        {label}
      </span>
    ))}
  </Flex>
)

const MonthlyChart = ({ monthly }) => {
  const data = {
    labels: monthly.map((row) => formatShortPeriod(row.period)),
    datasets: [
      {
        label: 'Cuotas pagadas a tiempo',
        data: monthly.map((row) => row.rate_percent),
        backgroundColor: monthly.map((row) => (row.with_rewards ? AFTER_COLOR : BEFORE_COLOR)),
        hoverBackgroundColor: monthly.map((row) => (row.with_rewards ? AFTER_COLOR : BEFORE_COLOR)),
        barPercentage: 0.6,
        categoryPercentage: 0.8,
        maxBarThickness: 32
      }
    ]
  }

  const options = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      displayColors: false,
      callbacks: {
        title: ([item]) => formatPeriod(monthly[item.index].period),
        label: (item) => {
          const row = monthly[item.index]
          return `${formatPercent(row.rate_percent)} a tiempo · ${row.on_time} de ${row.due} cuotas`
        },
        afterLabel: (item) => (monthly[item.index].with_rewards ? 'Con Rewards' : 'Antes de Rewards')
      }
    },
    scales: {
      yAxes: [
        {
          ticks: { min: 0, max: 100, stepSize: 25, callback: (value) => `${value}%`, fontColor: grays[600] },
          gridLines: { color: grays[200], zeroLineColor: grays[300], drawBorder: false }
        }
      ],
      xAxes: [{ ticks: { fontColor: grays[600], maxRotation: 0, autoSkip: true, autoSkipPadding: 16 }, gridLines: { display: false } }]
    }
  }

  return (
    <div style={{ height: 240 }}>
      <Bar data={data} options={options} />
    </div>
  )
}

const MonthlyTable = ({ monthly }) => (
  <Table size="sm" responsive className="fs--1 mb-0">
    <thead className="bg-200 text-900">
      <tr>
        <th>Mes</th>
        <th className="text-right">A tiempo</th>
        <th className="text-right">Cuotas</th>
        <th className="text-right">Puntualidad</th>
        <th>Programa</th>
      </tr>
    </thead>
    <tbody>
      {monthly.map((row) => (
        <tr key={row.period}>
          <td>{capitalize(formatPeriod(row.period))}</td>
          <td className="text-right">{formatNumber(row.on_time)}</td>
          <td className="text-right">{formatNumber(row.due)}</td>
          <td className="text-right font-weight-semi-bold">{formatPercent(row.rate_percent)}</td>
          <td>{row.with_rewards ? 'Con Rewards' : 'Antes'}</td>
        </tr>
      ))}
    </tbody>
  </Table>
)

// HO-TASK-288: la métrica central de la vista. Compara el porcentaje de cuotas
// pagadas antes del día límite en los meses previos al programa contra los
// meses con el programa activo.
const PunctualityImpact = ({ punctuality, deadlineDay }) => {
  const [showTable, setShowTable] = useState(false)
  const { before, after, monthly = [] } = punctuality || {}

  return (
    <Card className="mb-3">
      <CardBody>
        <h5 className="mb-1">Impacto en la puntualidad</h5>
        <p className="fs--1 text-600 mb-3">
          Porcentaje de cuotas pagadas antes del día {deadlineDay} de cada mes, antes y después de activar Homelan
          Rewards en el condominio.
        </p>

        <Row noGutters className="align-items-center">
          <Col xs={6} className="border-right border-200">
            <RateTile title="Antes de Rewards" stats={before} />
          </Col>
          <Col xs={6}>
            <RateTile title="Con Rewards" stats={after} highlight />
          </Col>
        </Row>
        <Delta before={before} after={after} />
        {before && after && after.periods < MIN_PERIODS_FOR_TREND && (
          <p className="fs--1 text-600 text-center mt-2 mb-0">
            El programa lleva {after.periods === 1 ? 'un mes' : `${after.periods} meses`} en el condominio: la
            comparación todavía es preliminar.
          </p>
        )}
        {!before && after && (
          <p className="fs--1 text-600 text-center mt-3 mb-0">
            No hay meses anteriores al programa registrados en Homelan para comparar.
          </p>
        )}

        {monthly.length > 0 && (
          <>
            <Flex justify="between" align="center" className="mt-4 mb-2 flex-wrap">
              <Legend monthly={monthly} />
              <Button color="link" size="sm" className="px-0" onClick={() => setShowTable((value) => !value)}>
                <FontAwesomeIcon icon={showTable ? faChartBar : faTable} className="mr-1" />
                {showTable ? 'Ver gráfico' : 'Ver tabla'}
              </Button>
            </Flex>
            {showTable ? <MonthlyTable monthly={monthly} /> : <MonthlyChart monthly={monthly} />}
          </>
        )}

        <p className="fs--2 text-500 mb-0 mt-3">
          La comparación muestra cómo cambió la puntualidad desde que arrancó el programa; otros factores del
          condominio también pueden influir.
        </p>
      </CardBody>
    </Card>
  )
}

PunctualityImpact.propTypes = { punctuality: PropTypes.object, deadlineDay: PropTypes.number }

export default PunctualityImpact
